const canCompleteLastWord = ({ parsed, nonParsed } = {}) =>
  (nonParsed && /\S$/.test(nonParsed) && !/"|'/.test(nonParsed) && !/ {2,}/.test(nonParsed)) ||
  (!nonParsed && /\w$/.test(parsed))

const canCompleteLastQuote = ({ nonParsed } = {}) => nonParsed && /^"([^"]*)$|^'([^']*)$/.test(nonParsed.trim())

const parserWordRegExp = /([\w"']+)|(<>)|([<>!]=)|([.,:=<>-])/giu

const processText = text => {
  const trimmed = text.trim()
  const words = trimmed.match(parserWordRegExp)

  if (words && words.length) {
    const firstWord = words[0]
    const lastWord = words[words.length - 1]
    const withoutFirstWord = trimmed.slice(firstWord.length).trim()
    const withoutLastWord = trimmed.slice(0, -lastWord.length).trim()

    return {
      words,
      trimmed,
      lastWord,
      firstWord,
      withoutFirstWord,
      withoutLastWord,
      endsWith: { letter: !/\s$/g.test(text) },
      startsWithLetter: !/^\s/g.test(text)
    }
  }

  return {
    trimmed,
    words: [],
    lastWord: '',
    firstWord: '',
    withoutFirstWord: '',
    withoutLastWord: '',
    endsWith: { letter: !/\s$/g.test(text) },
    startsWithLetter: !/^\s/g.test(text)
  }
}

const intersectionDistanceFromEnd = (baseString, comparedString) => {
  let distance = 0
  while (comparedString.length > distance) {
    const str = comparedString.slice(0, comparedString.length - distance)
    if (baseString.endsWith(str)) {
      return comparedString.length - distance
    }
    distance++
  }
  return 0
}

const insertString = ({ canBeCompleted, parserResult }) => {
  const {
    text,
    text: { beforeCursor, afterCursor }
  } = parserResult
  if (canBeCompleted) {
    if (canCompleteLastQuote(text)) {
      return {
        newTextBefore: beforeCursor.trim(),
        newTextAfter: afterCursor.trim()
      }
    }
  } else {
    return {
      newTextBefore: beforeCursor.trim(),
      newTextAfter: afterCursor.trim()
    }
  }
  return null
}

const insertValue = ({ parserResult }) => {
  return {
    newTextBefore: parserResult.text.parsed,
    newTextAfter: parserResult.text.afterCursor.trim()
  }
}

const insertOperator = ({ canBeCompleted, token, label, parserResult }) => {
  const {
    text: { beforeCursor, afterCursor }
  } = parserResult

  if ('STR' === token) {
    return insertString({ canBeCompleted, parserResult })
  }

  const textBefore = processText(beforeCursor)
  const textAfter = processText(afterCursor)

  if (canBeCompleted) {
    const textLabel = processText(label)
    if (!textLabel.withoutFirstWord) {
      const lastWordCI = textBefore.lastWord.toLowerCase()

      if (label.startsWith(lastWordCI) && lastWordCI !== label) {
        return {
          fuzzy: textBefore.lastWord,
          fuzzyIndexOf: 0,
          offsetText: textBefore.lastWord,
          newTextBefore: textBefore.withoutLastWord,
          newTextAfter: textAfter.startsWithLetter ? textAfter.withoutFirstWord : textAfter.trimmed
        }
      }

      return null
    } else {
      const lower = beforeCursor.toLowerCase()
      const distance = intersectionDistanceFromEnd(lower, label)

      if (distance) {
        const char = beforeCursor[beforeCursor.length - 1 - distance]
        if (!/[a-z]/i.test(char)) {
          const tail = label.slice(distance)
          return {
            fuzzy: beforeCursor.slice(-distance),
            fuzzyIndexOf: 0,
            offsetText: beforeCursor.slice(-distance),
            newTextBefore: beforeCursor.slice(0, -distance).trim(),
            newTextAfter: afterCursor.toLowerCase().startsWith(tail)
              ? afterCursor.slice(tail.length).trim()
              : textAfter.trimmed
          }
        }
      }
    }
    return null
  }

  const newTextAfter =
    label.length > textAfter.trimmed.length
      ? label.startsWith(textAfter.trimmed)
        ? ''
        : textAfter.trimmed
      : textAfter.trimmed.startsWith(label)
      ? textAfter.trimmed.slice(label.length).trim()
      : textAfter.trimmed

  return {
    newTextBefore: textBefore.trimmed,
    newTextAfter
  }
}

const insertByReplaceLastWord = ({ canBeCompleted, label, parserResult, startsWith }) => {
  const {
    text,
    text: { beforeCursor, afterCursor }
  } = parserResult

  const textBefore = processText(beforeCursor)
  const textAfter = processText(afterCursor)

  if (canBeCompleted) {
    if (canCompleteLastWord(text) && textBefore.lastWord.length < label.length) {
      const lastWordCI = textBefore.lastWord.toLowerCase()
      const labelCI = label.toLowerCase()
      const indexOf = labelCI.indexOf(lastWordCI)

      const canComplete = startsWith
        ? 0 === indexOf && lastWordCI !== labelCI
        : -1 !== indexOf && lastWordCI !== labelCI

      if (canComplete) {
        return {
          fuzzy: textBefore.lastWord,
          fuzzyIndexOf: indexOf,
          offsetText: textBefore.lastWord,
          newTextBefore: textBefore.withoutLastWord,
          newTextAfter: textAfter.startsWithLetter ? textAfter.withoutFirstWord : textAfter.trimmed
        }
      }
    }
  } else {
    const newTextAfter =
      label.length > textAfter.firstWord.length
        ? label.startsWith(textAfter.firstWord)
          ? textAfter.withoutFirstWord
          : textAfter.trimmed
        : textAfter.trimmed.startsWith(label)
        ? textAfter.trimmed.slice(label.length).trim()
        : textAfter.trimmed

    return {
      newTextBefore: textBefore.trimmed,
      newTextAfter
    }
  }

  return null
}

export const LABEL_RULES = {
  uid: {
    denied: {
      after: ['GT', 'GE', 'LT', 'LE']
    }
  },
  completed: {
    allowed: {
      before: ['OR', 'TOKEN', 'AND', 'NOT', 'LPAREN', 'RPAREN', 'EQ', 'NEQ', 'LE', 'GE', 'LT', 'GT', 'EOF'],
      after: ['OR', 'TOKEN', 'AND', 'NOT', 'LPAREN', 'RPAREN', 'EQ', 'NEQ', 'LE', 'GE', 'LT', 'GT']
    }
  }
}

export default function insertToken(type, { canBeCompleted, token, label, parserResult }) {
  const lastToken = parserResult.tokens.type.last
  const lastLabel = parserResult.tokens.match.last

  if ('operator' == type) {
    if ('UID' === lastLabel && -1 !== LABEL_RULES.uid.denied.after.indexOf(token)) {
      return null
    }

    if ('COMPLETED' === lastLabel && -1 === LABEL_RULES.completed.allowed.after.indexOf(token)) {
      return null
    }

    return insertOperator({ canBeCompleted, token, label, parserResult })
  }

  if ('value' === type) {
    return insertValue({ parserResult })
  }

  if ('question' == type) {
    return insertByReplaceLastWord({ canBeCompleted, label, parserResult, startsWith: true })
  }

  if ('variable' == type) {
    if ('uid' === label && 'SUB' === lastToken) {
      return null
    }

    if ('completed' === label && -1 === LABEL_RULES.completed.allowed.before.indexOf(lastToken)) {
      return null
    }

    return insertByReplaceLastWord({ canBeCompleted, token, label, parserResult })
  }

  if ('codebook' === type) {
    return insertByReplaceLastWord({ canBeCompleted, token, label, parserResult })
  }

  if ('tag' === type) {
    return insertByReplaceLastWord({ canBeCompleted, token, label, parserResult })
  }

  return null
}
